import { Skeleton, Tooltip } from '@mui/material';
import { useFeatureFlags } from 'apps/wrapper/src/hooks/feature-flags/use-feature-flags';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { AppointmentRequestSummary } from 'apps/wrapper/src/services/appointment/types/appointment-request-summary';
import moment from 'moment';
import { FC, useMemo } from 'react';
import { useApptRequestsIv } from '../../../../../hooks/use-appt-requests-iv';
import { IvStatusIcon } from './iv-status.styles';

export interface IvStatusProps {
  appointmentRequest: AppointmentRequestSummary;
}

export const IvStatus: FC<IvStatusProps> = ({ appointmentRequest }) => {
  const { selectedLocation } = useLocations();
  const { hasFeature } = useFeatureFlags();
  const { isLoadingIv, patientIvSummaries, apptRequestIvSummaries, ivStatusesToProcess } = useApptRequestsIv();

  if (!appointmentRequest) return null;

  if (!selectedLocation) return null;

  if (!hasFeature(selectedLocation.id, 'InsuranceVerification2')) return null;

  if (!ivStatusesToProcess.includes(appointmentRequest.status)) return null;

  const ivSummary = useMemo(
    () =>
      appointmentRequest.patient?.id &&
      (!appointmentRequest.isDirectlyBooked ||
        (appointmentRequest.isDirectlyBooked && !appointmentRequest.isNewPatientCreated))
        ? patientIvSummaries.find((x) => x.patientId === appointmentRequest.patient.id)?.primaryVerificationSummary
        : apptRequestIvSummaries.find((x) => x.appointmentRequestId === appointmentRequest.id)?.verificationSummary,
    [patientIvSummaries, apptRequestIvSummaries]
  );

  const ivStatus = ivSummary?.ivSummaryStatus;

  const isInsuranceVerified = useMemo(
    () => ivStatus === 'InsuranceVerified' || ivStatus === 'ManuallyVerified' || ivStatus === 'SelfPay',
    [ivStatus]
  );

  const isInsuranceInProgress = useMemo(() => ivStatus === 'InsuranceInProcess' || !ivStatus, [ivStatus]);
  const createdDate = moment(appointmentRequest.createdDate);
  const twoHoursAgo = moment().subtract(2, 'hours');

  const isVerificationExpired = useMemo(
    () => !ivSummary && !createdDate.isAfter(twoHoursAgo, 'day'),
    [ivSummary, createdDate, twoHoursAgo]
  );

  return (
    <>
      {!ivSummary && isLoadingIv && (
        <Skeleton variant="circular" width={16} height={16} sx={{ margin: '0 0 0 auto', display: 'flex' }} />
      )}

      {isVerificationExpired && !isLoadingIv && <IvStatusIcon verified="false" inprogress="true" />}

      {!isVerificationExpired && ivSummary && !isLoadingIv && (
        <Tooltip placement="left" title={isInsuranceInProgress ? 'Verification in Process' : null}>
          <IvStatusIcon verified={isInsuranceVerified.toString()} inprogress={isInsuranceInProgress.toString()} />
        </Tooltip>
      )}
    </>
  );
};
